import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useDispatch, useSelector } from "react-redux"
import DeleteModal from "components/Common/DeleteModal"
import SAlert from "components/Alert"
import AdjustmentDetailModal from "components/Adjustment/details"
import AdjustmentListComponent from "components/Adjustment/list"
import {
  getAdjustments,
  deleteAdjustment,
  deleteManyAdjustments,
  exportAdjustments,
} from "store/adjustment/adjustment-action"
import useSearch from "hooks/useSearch"

const AdjustmentList = () => {
  document.title = "Adjustments"

  const dispatch = useDispatch()
  const { data, error, loading } = useSelector(state => state.adjustment)
  const permissions = useSelector(state => state.auth.permissions)
  const [deleteModal, setDeleteModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [search, setSearch] = useState(null)
  const [pageNumber, setPageNumber] = useState(10)
  const [according, setAccording] = useState({ order: "desc", name: "id" })
  const [detailModal, setDetailModal] = useState(false)

  const handleDeleteFunc = () => {
    if (selected) {
      dispatch(deleteAdjustment(selected.id))
    }
    setDeleteModal(false)
  }

  const onPageClick = link => {
    dispatch(
      getAdjustments(link.url, pageNumber, according, search, advnacedSearch)
    )
  }

  const onSearchHandler = value => setSearch(value)
  const onChangeRowNumber = value => setPageNumber(value)
  const onChangeOrdering = value => {
    if (value.order) setAccording(prev => ({ ...prev, order: value.order }))
    if (value.name) setAccording(prev => ({ ...prev, name: value.name }))
  }

  const [advnacedSearch, setAdvancedSearch] = useState({})

  useSearch(
    getAdjustments(undefined, undefined, undefined, search, advnacedSearch),
    search
  )

  useEffect(() => {
    dispatch(
      getAdjustments(undefined, pageNumber, according, search, advnacedSearch)
    )
  }, [pageNumber, according])

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteFunc}
        onCloseClick={() => setDeleteModal(false)}
      />

      <AdjustmentDetailModal
        open={detailModal}
        setOpen={setDetailModal}
        data={selected}
      />

      <Breadcrumbs title={"Provider"} breadcrumbItem={"Adjustments"} />
      <AdjustmentListComponent
        data={data}
        error={error}
        loading={loading}
        permissions={permissions}
        onPageClick={onPageClick}
        onSearch={onSearchHandler}
        onChangeRowNumber={onChangeRowNumber}
        onChangeOrdering={onChangeOrdering}
        onClickDelete={row => {
          // Validate before allowing deletion
          if (row.adjustment_type === "Increase") {
            // If it was an increase, deleting it means we need to subtract from current balance
            const currentBalance = row?.provider_in?.in_qty_per_ip || 0
            const adjustmentQty = row?.qty || 0
            
            if (currentBalance < adjustmentQty) {
              SAlert({
                text: `Cannot delete this adjustment. Current balance (${currentBalance}) is less than adjustment quantity (${adjustmentQty}). Deleting would result in negative balance.`,
                status: "error",
              })
              return
            }
          }
          // For Decrease type, deleting adds back to stock, so no validation needed
          
          setSelected(row)
          setDeleteModal(true)
        }}
        onAllDeleteClicked={ids => {
          if (ids.length === 0) {
            SAlert({ text: "no adjustment selected", status: "info" })
            return
          }
          
          // Validate all selected adjustments before deletion
          const selectedAdjustments = data?.data?.filter(adj => ids.includes(adj.id))
          const invalidAdjustments = []
          
          selectedAdjustments?.forEach(row => {
            if (row.adjustment_type === "Increase") {
              const currentBalance = row?.provider_in?.in_qty_per_ip || 0
              const adjustmentQty = row?.qty || 0
              
              if (currentBalance < adjustmentQty) {
                invalidAdjustments.push({
                  item: row?.item?.item_name,
                  batch: row?.provider_in?.batch_number,
                  currentBalance,
                  adjustmentQty
                })
              }
            }
          })
          
          if (invalidAdjustments.length > 0) {
            const itemsList = invalidAdjustments.map(
              inv => `${inv.item} (Batch: ${inv.batch}, Balance: ${inv.currentBalance}, Adjustment: ${inv.adjustmentQty})`
            ).join(", ")
            
            SAlert({
              text: `Cannot delete some adjustments. The following items would have negative balance: ${itemsList}`,
              status: "error",
            })
            return
          }
          
          dispatch(deleteManyAdjustments(ids))
        }}
        onAdvancedSearchSubmit={data => {
          setAdvancedSearch(data)
          dispatch(
            getAdjustments(undefined, undefined, undefined, search, data)
          )
        }}
        onClickDetail={row => {
          setSelected(row)
          setDetailModal(true)
        }}
        exportStatus={useSelector(state => state.adjustment.exportStatus)}
        onExportBtnClick={() => {
          dispatch(
            exportAdjustments(pageNumber, according, search, advnacedSearch)
          )
        }}
      />
    </React.Fragment>
  )
}

export default AdjustmentList
